import React, { useState, useMemo } from "react";
import { colors, displayName } from "../utils/theme.js";
import { api } from "../api/client.js";
import { inputStyle, primaryBtn, ghostBtn } from "../clients/ui.js";
import { defaultPmFor } from "../clients/clientLogic.js";

// Create a task from the Employees page (increment 6). Client work (client → project →
// objective) or Sawtooth-internal (no client; optional internal objective, created here).
// Same records as tasks created on a client page.
const UNASSIGNED = "__unassigned__";

export default function TaskCreate({ users, clients, projects, objectives, defaultAssigneeId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [assigneeId, setAssigneeId] = useState(defaultAssigneeId || UNASSIGNED);
  const [assigneeTouched, setAssigneeTouched] = useState(false);
  const [workType, setWorkType] = useState("client");
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [objectiveId, setObjectiveId] = useState("");
  const [newObjective, setNewObjective] = useState("");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [instructions, setInstructions] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isClient = workType === "client";
  const clientProjects = useMemo(() => projects.filter((p) => p.clientId === clientId), [projects, clientId]);
  const objectiveOptions = useMemo(
    () => (isClient ? objectives.filter((o) => o.clientId === clientId) : objectives.filter((o) => !o.clientId)),
    [objectives, isClient, clientId],
  );

  // Client work defaults the assignee to the client's (or chosen project's) PM until the user
  // picks someone themselves; no PM (or internal work) falls back to the opening default.
  const applyDefaultAssignee = (next) => {
    if (assigneeTouched) return;
    const pm = next.isClient ? defaultPmFor(projects, next) : null;
    setAssigneeId(pm || defaultAssigneeId || UNASSIGNED);
  };

  const L = ({ children }) => <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: colors.textDim, margin: "12px 0 4px" }}>{children}</div>;
  const valid = name.trim() && (!isClient || clientId);

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setError(null);
    try {
      let objId = objectiveId || null;
      if (newObjective.trim()) {
        const or = await api.objectives.create({
          name: newObjective.trim(),
          clientId: isClient ? (clientId || null) : null,
          projectId: isClient ? (projectId || null) : null,
        });
        if (!or.ok) { setError(or.data?.error || "Couldn’t create the objective."); setBusy(false); return; }
        objId = or.data.id;
      }
      const assignee = assigneeId === UNASSIGNED ? { type: "unassigned" } : { type: "user", userId: assigneeId };
      const tr = await api.tasks.create({
        name: name.trim(), assignee,
        clientId: isClient ? (clientId || null) : null,
        projectId: isClient ? (projectId || null) : null,
        objectiveId: objId,
        startDate: startDate || null, dueDate: dueDate || null,
        instructions,
        private: isClient && isPrivate,
      });
      if (!tr.ok) { setError(tr.data?.error || "Couldn’t create the task."); setBusy(false); return; }
      onCreated();
    } catch (e) {
      setError(e?.message || "Couldn’t create the task."); setBusy(false);
    }
  };

  const toggle = (active) => ({
    flex: 1, padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontSize: 13, textAlign: "center",
    border: `1px solid ${active ? colors.accent : colors.borderLight}`, background: active ? colors.accentDim : colors.bg,
    color: active ? colors.text : colors.textMuted,
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ width: 520, maxHeight: "88vh", overflow: "auto", background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 28 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700, color: colors.text }}>New task</h2>

        <L>Task name *</L>
        <input autoFocus style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="What needs doing" />

        <L>Assign to</L>
        <select style={inputStyle} value={assigneeId} onChange={(e) => { setAssigneeId(e.target.value); setAssigneeTouched(true); }}>
          <option value={UNASSIGNED}>Unassigned</option>
          {(users || []).map((u) => <option key={u.id} value={u.id}>{displayName(u)}</option>)}
        </select>

        <L>Type of work</L>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={toggle(isClient)} onClick={() => { setWorkType("client"); applyDefaultAssignee({ isClient: true, clientId, projectId }); }}>Client work</div>
          <div style={toggle(!isClient)} onClick={() => { setWorkType("internal"); applyDefaultAssignee({ isClient: false }); }}>Sawtooth-internal</div>
        </div>

        {isClient && (
          <>
            <L>Client *</L>
            <select style={inputStyle} value={clientId} onChange={(e) => { setClientId(e.target.value); setProjectId(""); setObjectiveId(""); applyDefaultAssignee({ isClient: true, clientId: e.target.value }); }}>
              <option value="">Select client</option>
              {[...clients].sort((a, b) => a.name.localeCompare(b.name)).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {clientId && (
              <>
                <L>Project</L>
                <select style={inputStyle} value={projectId} onChange={(e) => { setProjectId(e.target.value); applyDefaultAssignee({ isClient: true, clientId, projectId: e.target.value }); }}>
                  <option value="">None</option>
                  {clientProjects.map((p) => <option key={p.id} value={p.id}>{p.projectType === "proposal-plan" ? "6-Month Proposal Plan" : (p.customTitle || p.projectType || p.id)}</option>)}
                </select>
              </>
            )}
          </>
        )}

        <L>Objective</L>
        <select style={inputStyle} value={objectiveId} onChange={(e) => setObjectiveId(e.target.value)} disabled={!!newObjective.trim()}>
          <option value="">None</option>
          {objectiveOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <input style={{ ...inputStyle, marginTop: 6 }} value={newObjective} onChange={(e) => setNewObjective(e.target.value)}
          placeholder={isClient ? "…or create a new objective" : "…or create a new internal objective"} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><L>Start date</L><input type="date" style={inputStyle} value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
          <div><L>Due date</L><input type="date" style={inputStyle} value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
        </div>

        <L>Instructions</L>
        <textarea style={{ ...inputStyle, minHeight: 70, resize: "vertical" }} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="How to complete this task (optional)" />

        {isClient && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 13, color: colors.textMuted, cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
            Private — hide from client updates
          </label>
        )}
        {error && <div style={{ color: colors.danger, fontSize: 13, marginTop: 10 }}>{error}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={submit} disabled={!valid || busy} style={{ ...primaryBtn, opacity: !valid || busy ? 0.5 : 1, cursor: !valid || busy ? "not-allowed" : "pointer" }}>{busy ? "Creating…" : "Create task"}</button>
        </div>
      </div>
    </div>
  );
}
